import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { handleApiError } from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import { devicesApi, deviceKeys, type Device } from '../api'
import { DeviceRow } from './DeviceRow'

/**
 * A user's sign-in devices, for an administrator (users.devices.read): every device ever enrolled,
 * revoked ones included, with how each was enrolled, where it last signed in from, and who revoked
 * it and why. Revoking (users.devices.revoke) needs a reason, and is written to the audit log
 * before it is done.
 */
export function UserDevicesDialog({ user, open, onClose }: { user: { id: number; name?: string; email: string } | null; open: boolean; onClose: () => void }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const mayRevoke = useCan('users.devices.revoke')
    const [revoking, setRevoking] = useState<Device | null>(null)
    const [reason, setReason] = useState('')
    const userId = user?.id ?? 0
    const { data, isLoading, isError, error } = useQuery({
        queryKey: deviceKeys.forUser(userId),
        queryFn: () => devicesApi.listForUser(userId),
        enabled: open && userId > 0,
    })
    const revoke = useMutation({
        mutationFn: () => devicesApi.revokeForUser(userId, revoking!.id, reason.trim()),
        onSuccess: () => {
            setRevoking(null)
            setReason('')
            queryClient.invalidateQueries({ queryKey: deviceKeys.forUser(userId) })
        },
    })
    const devices = data?.devices ?? []
    const activeCount = devices.filter((d) => d.status === 'active').length

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) { setRevoking(null); setReason(''); onClose() } }}>
            <DialogContent className="sm:max-w-xl" data-testid="user-devices-dialog">
                <DialogHeader>
                    <DialogTitle>{t('devices.admin.title', { name: user?.name || user?.email || '' })}</DialogTitle>
                    <DialogDescription>{t('devices.admin.description')}</DialogDescription>
                </DialogHeader>
                {isLoading && <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>}
                {isError && <p className="text-sm text-destructive" role="alert">{handleApiError(error)}</p>}
                {data && devices.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-devices">{t('devices.admin.none')}</p>}
                {devices.length > 0 && (
                    <ul className="max-h-[50vh] space-y-2 overflow-y-auto">
                        {devices.map((device) => (
                            <DeviceRow
                                key={device.id}
                                device={device}
                                showAudit
                                action={mayRevoke && device.status === 'active' ? (
                                    <Button variant="outline" size="sm" onClick={() => { setRevoking(device); setReason(''); revoke.reset() }}
                                        aria-label={t('devices.admin.revokeNamed', { name: device.name })}>
                                        {t('devices.admin.revoke')}
                                    </Button>
                                ) : undefined}
                            />
                        ))}
                    </ul>
                )}
                {revoking && (
                    <form
                        className="space-y-2 rounded-md border border-destructive/40 p-3"
                        data-testid="revoke-form"
                        onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 10) revoke.mutate() }}
                    >
                        <p className="text-sm font-medium">{t('devices.admin.revokeTitle', { name: revoking.name })}</p>
                        {activeCount === 1 && <p className="text-xs text-destructive" data-testid="last-device-warning">{t('devices.admin.lastDeviceWarning')}</p>}
                        <Label htmlFor="revoke-reason">{t('devices.admin.reasonLabel')}</Label>
                        <Input id="revoke-reason" value={reason} maxLength={255} onChange={(e) => setReason(e.target.value)} />
                        {revoke.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(revoke.error)}</p>}
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="ghost" size="sm" onClick={() => setRevoking(null)} disabled={revoke.isPending}>{t('common.cancel')}</Button>
                            <Button type="submit" variant="destructive" size="sm" disabled={revoke.isPending || reason.trim().length < 10}>{t('devices.admin.revoke')}</Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}
